import { useEffect, useRef, type RefObject } from 'react';
import type L from 'leaflet';
import type { Feature, Polygon, MultiPolygon } from 'geojson';
import { useUIStore } from '../../store/uiStore';
import { useServiceAreasStore } from '../../store/serviceAreasStore';
import { getLeaflet } from './leafletHelpers';
import { createPrefixedLogger } from '../../utils/logger';
import {
  CASING_EXTRA_WEIGHT,
  FIRE_ERA_STYLE,
  FIRE_HISTORY_CASING,
  FIRE_HISTORY_CASING_OPACITY,
  FIRE_HISTORY_STROKE,
  fireEra,
} from './fireHistoryColors';

const log = createPrefixedLogger('map:fire-history');

interface FireHistoryProperties {
  year: number;
  name: string | null;
  acres: number;
}

interface FireHistoryFeatureCollection {
  type: 'FeatureCollection';
  features: Feature<Polygon | MultiPolygon, FireHistoryProperties>[];
}

function fireHistoryUrl(areaId: string): string {
  return `/fire-history/${encodeURIComponent(areaId)}.geojson`;
}

// Its own pane, above fhszPane (350) so perimeter outlines read over hazard
// fills, below overlayPane (400, service-area outlines) and the marker pane
// (600). Sharing a pane with either would be a bug, not a saving: all three
// overlay hooks hide by pane display, so one toggle would blank another's
// layers.
const FIRE_HISTORY_PANE = 'fireHistoryPane';
const FIRE_HISTORY_PANE_Z_INDEX = '360';

/**
 * Render CAL FIRE historical fire perimeters for every active service area,
 * shown/hidden en masse by `uiStore.fireHistoryEnabled`.
 *
 * Modelled on useFhszOverlay: one layer group per area built on first
 * enable, cached in a ref, toggled by pane display rather than removed so
 * re-enabling never re-fetches, and a missing export 404s once and is
 * remembered. The two hooks are close to identical and could share a
 * `useStaticGeojsonOverlay`; that extraction is worth doing when a third
 * overlay lands, not now, when it would only put the shipped FHSZ path back
 * under test for nothing the user can see.
 *
 * Perimeters are drawn as outlines, never fills — FHSZ owns fills, and two
 * translucent fills stacked would say nothing. Each perimeter is stroked
 * twice: a white casing underneath so the line survives satellite imagery,
 * and the plum stroke on top, weighted and faded by age.
 */
export function useFireHistoryOverlay({
  mapRef,
  overlayLayerRef,
  isLoaded,
}: {
  mapRef: RefObject<L.Map | null>;
  overlayLayerRef: RefObject<L.LayerGroup | null>;
  isLoaded: boolean;
}): void {
  const enabled = useUIStore((s) => s.fireHistoryEnabled);
  const areas = useServiceAreasStore((s) => s.areas);
  const activeAreaIdsKey = areas
    .filter((a) => a.active)
    .map((a) => a.id)
    .join(',');

  const layersRef = useRef<Map<string, L.LayerGroup>>(new Map());
  const missesRef = useRef<Set<string>>(new Set());
  const loadingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const overlay = overlayLayerRef.current;
    const map = mapRef.current;
    if (!overlay || !map || !isLoaded || !enabled) return;
    const activeAreaIds = activeAreaIdsKey ? activeAreaIdsKey.split(',') : [];

    // Everything this hook draws lives in one pane, so visibility is a single
    // style write rather than the per-layer walk useFhszOverlay does.
    const setVisible = (visible: boolean) => {
      const pane = map.getPane(FIRE_HISTORY_PANE);
      if (pane) pane.style.display = visible ? '' : 'none';
    };

    const buildLayer = (areaId: string, collection: FireHistoryFeatureCollection) => {
      if (layersRef.current.has(areaId)) return;
      const L = getLeaflet();
      if (!map.getPane(FIRE_HISTORY_PANE)) {
        map.createPane(FIRE_HISTORY_PANE).style.zIndex = FIRE_HISTORY_PANE_Z_INDEX;
      }
      const eraStyle = (feature?: Feature<Polygon | MultiPolygon, FireHistoryProperties>) =>
        FIRE_ERA_STYLE[fireEra(feature?.properties?.year ?? 0)];

      const casing = L.geoJSON(collection as never, {
        pane: FIRE_HISTORY_PANE,
        interactive: false,
        style: (feature) => {
          const era = eraStyle(feature as never);
          return {
            fill: false,
            color: FIRE_HISTORY_CASING,
            weight: era.weight + CASING_EXTRA_WEIGHT,
            opacity: FIRE_HISTORY_CASING_OPACITY,
            dashArray: era.dashArray,
          };
        },
      });
      const outline = L.geoJSON(collection as never, {
        pane: FIRE_HISTORY_PANE,
        interactive: false,
        style: (feature) => {
          const era = eraStyle(feature as never);
          return {
            fill: false,
            color: FIRE_HISTORY_STROKE,
            weight: era.weight,
            opacity: era.opacity,
            dashArray: era.dashArray,
          };
        },
      });

      // Casing first so the plum stroke paints over it.
      const group = L.layerGroup([casing, outline]);
      group.addTo(overlay);
      layersRef.current.set(areaId, group);
    };

    let cancelled = false;
    for (const areaId of activeAreaIds) {
      if (
        layersRef.current.has(areaId) ||
        missesRef.current.has(areaId) ||
        loadingRef.current.has(areaId)
      ) {
        continue;
      }
      loadingRef.current.add(areaId);
      fetch(fireHistoryUrl(areaId))
        .then((res) => {
          if (!res.ok) {
            if (res.status === 404) {
              missesRef.current.add(areaId);
              return null;
            }
            throw new Error(`HTTP ${res.status}`);
          }
          return res.json() as Promise<FireHistoryFeatureCollection>;
        })
        .then((collection) => {
          if (cancelled || !collection) return;
          buildLayer(areaId, collection);
          setVisible(useUIStore.getState().fireHistoryEnabled);
        })
        .catch((error) => {
          log.warn(`Failed to load fire history overlay for ${areaId}`, error);
        })
        .finally(() => {
          loadingRef.current.delete(areaId);
        });
    }

    setVisible(true);
    return () => {
      cancelled = true;
    };
  }, [enabled, activeAreaIdsKey, isLoaded, mapRef, overlayLayerRef]);

  // Hide (never remove) once disabled — keeps every fetched layer cached so
  // re-enabling the toggle is instant.
  useEffect(() => {
    if (enabled) return;
    const pane = mapRef.current?.getPane(FIRE_HISTORY_PANE);
    if (pane) pane.style.display = 'none';
  }, [enabled, mapRef]);
}
